<template>
  <v-card>
    <v-card-title>
      {{ title }}
    </v-card-title>
    <v-card-text>
      <v-row class="align-center justify-right">
        <v-col
          cols="6"
          md="3"
        >
          <search-field
            v-model="templateSearch"
            autofocus
          />
        </v-col>
        <v-col
          cols="6"
          md="9"
          class="text-right"
        >
          <add-new-button @click="addTemplate()" />
        </v-col>
      </v-row>

      <v-data-table
        :headers="templateHeaders"
        :items="templates"
        :search="templateSearch"
        :sort-by="['name']"
        item-key=".key"
        class="cursor-pointer data-table-td-caption"
        hide-default-footer
      >
        <template #[`item.name`]="{ item }">
          <v-tooltip bottom>
            <template #[`activator`]="{ on }">
              <span v-on="on">{{ item.name }}</span>
            </template>
            <span>Id: {{ item['.key'] }}</span>
          </v-tooltip>
        </template>

        <template #[`item.formstack`]="{ item }">
          <div class="caption">
            <v-tooltip
              v-if="'formstack' in item"
              bottom
            >
              <template #[`activator`]="{ on }">
                <span
                  class="text-no-wrap"
                  v-on="on"
                >
                  <a
                    :href="item.formstack.url"
                    target="_blank"
                    class="secondary--text"
                  >Formstack</a>
                  <v-icon
                    class="ml-1 caption"
                    color="primary"
                    v-text="'mdi-open-in-new'"
                  />
                </span>
              </template>
              <span>Formstack Id: {{ item.formstack.id }} / <span>{{ formstackUpdated(item) }}</span></span>
            </v-tooltip>
            <br>
            <v-tooltip
              v-if="'webmerge' in item && item.webmerge.url"
              bottom
            >
              <template #[`activator`]="{ on }">
                <span
                  class="text-no-wrap"
                  v-on="on"
                >
                  <a
                    :href="item.webmerge.url"
                    target="_blank"
                    class="secondary--text"
                  >Webmerge</a><v-icon
                    class="ml-1 caption"
                    color="primary"
                  >
                    mdi-open-in-new
                  </v-icon>
                </span>
              </template>
              <span>Webmerge Id: {{ item.webmerge.id }}</span>
            </v-tooltip>
          </div>
        </template>

        <template #[`item.active`]="{ item }">
          <span
            v-if="$gnp(item.formstack, ['formDef']) !== 'error'"
            :class="{ 'font-weight-bold': !item.active }"
          >{{ item.active }}
            <v-chip
              v-if="item.preview"
              color="primary"
              x-small
            >
              preview
            </v-chip>
          </span>
          <span
            v-else
            class="font-weight-bold"
          >
            error<br>
            <v-icon color="error">mdi-exclamation</v-icon>
          </span>
          <span
            v-show="item.status"
            class="font-italic"
          >{{
            item.status
          }}</span>
        </template>

        <template #[`item.created`]="{ item }">
          <v-tooltip bottom>
            <template #[`activator`]="{ on }">
              <span
                class="text-no-wrap"
                v-on="on"
              >{{
                $formatFsTimestamp(item.created)
              }}</span>
            </template>
            <span>{{
              item.created && item.created.toDate().toLocaleString()
            }}</span>
          </v-tooltip>
        </template>

        <template #[`item.modified`]="{ item }">
          <v-tooltip bottom>
            <template #[`activator`]="{ on }">
              <span
                class="text-no-wrap"
                v-on="on"
              >{{
                $formatFsTimestamp(item.modified)
              }}</span>
            </template>
            <span>{{
              item.modified && item.modified.toDate().toLocaleString()
            }}</span>
          </v-tooltip>
        </template>

        <template #[`item.actions`]="{ item }">
          <v-icon
            small
            @click="editTemplate(item)"
          >
            mdi-pencil
          </v-icon>
        </template>
      </v-data-table>

      <v-dialog
        v-model="editTemplateDialog"
        scrollable
        max-width="500px"
      >
        <edit-template
          :allow-category-selection="allowCategorySelection"
          :selected-template="selectedTemplate"
          :app-template-categories="categories"
          :lawyers="lawyers"
          :is-open="editTemplateDialog"
          @cancel="editTemplateDialog = false"
          @templateSaved="editTemplateDialog = false"
          @templateDeleted="editTemplateDialog = false"
        />
      </v-dialog>
    </v-card-text>
  </v-card>
</template>
<script>
import AddNewButton from './AddNewButton.vue'
import EditTemplate from './EditTemplate.vue'
import SearchField from './SearchField.vue'
import { listLawyers } from '@/dataAccess/users'

const templateBase = {
  name: '',
  categories: [],
  active: false,
  formstack: { name: '', id: '' },
  webmerge: { name: '', id: '' },
  expertLawyers: []
}

export default {
  name: 'TemplatesTemplateList',
  components: { AddNewButton, EditTemplate, SearchField },
  props: {
    title: { type: String, required: true },
    categories: { type: Array, default: () => [] },
    templates: { type: Array, required: true },
    newTemplateDefaults: { type: Object, default: () => ({}) },
    allowCategorySelection: { type: Boolean, default: false }
  },
  data: () => ({
    editTemplateDialog: false,
    selectedTemplate: null,
    templateSearch: null,
    templateHeaders: [
      {
        text: 'Name',
        value: 'name',
        align: 'left',
        sortable: true
      },
      {
        text: 'External',
        value: 'formstack',
        align: 'left',
        sortable: false
      },
      {
        text: 'Active',
        value: 'active',
        align: 'left',
        sortable: true
      },
      {
        text: 'Created',
        value: 'created',
        align: 'left',
        sortable: true
      },
      {
        text: 'Modified',
        value: 'modified',
        align: 'left',
        sortable: true
      },
      {
        text: ' ',
        value: 'actions',
        align: 'center',
        sortable: false
      }
    ]
  }),
  beforeCreate() {
    this.$binding('lawyers', listLawyers())
  },
  methods: {
    editTemplate(template) {
      this.selectedTemplate = template
      this.editTemplateDialog = true
    },
    addTemplate() {
      this.editTemplate({ ...templateBase, ...this.newTemplateDefaults })
    },
    formstackUpdated(template) {
      return template.formstack.updated &&
          template.formstack.updated.toDate &&
          template.formstack.updated.toDate().toLocaleString()
    }
  }
}
</script>
